import { css } from "@emotion/css";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { OctopusError } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import { LoadingImage } from "~/components/Images/LoadingCowboy/LoadingImage";

interface LoadingScreenProps {
    inProgressMessage: string;
    errorDetails: OctopusError | null;
}

export default function LoadingScreen({ inProgressMessage, errorDetails }: LoadingScreenProps) {
    const [showFullErrorMessage, setShowFullErrorMessage] = useState<boolean>(false);

    const toggleFullErrorMessage = () => {
        setShowFullErrorMessage(!showFullErrorMessage);
    };

    return (
        <div className={styles.container}>
            <div className={styles.loading}>
                <div className={styles.inProgressMessage}>{inProgressMessage}</div>
                <LoadingImage />
                {errorDetails && (
                    <div className={styles.errorInfoPanel}>
                        <button type="button" className={styles.toggleErrorButton} onClick={toggleFullErrorMessage}>
                            {showFullErrorMessage ? "Hide details" : "Show details"}
                        </button>
                    </div>
                )}
            </div>

            {showFullErrorMessage && (
                <div>
                    {errorDetails && (
                        <div className={styles.errorInfoPanelDetails}>
                            <h3>{errorDetails.ErrorMessage}</h3>
                            <div className={styles.errorMessage}>
                                {errorDetails.Errors &&
                                    (errorDetails.Errors.length > 1 ? (
                                        <div>
                                            Errors:{" "}
                                            {errorDetails.Errors.map((x) => (
                                                <li>{x}</li>
                                            ))}
                                        </div>
                                    ) : (
                                        <div>{errorDetails.Errors[0]}</div>
                                    ))}
                                {errorDetails.FullException && <div>FullException: {errorDetails.FullException}</div>}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const styles = {
    container: css({
        textAlign: "center",
        display: "flex",
        height: "100vh",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: themeTokens.color.background.primary.default,
    }),
    // This is temporarily duplicating button styles to decouple this loading screen from the design system components.
    // This file will be removed when the new initial loading feature replaces it.
    toggleErrorButton: css({
        all: "unset",
        font: text.body.bold.medium,
        width: "fit-content",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        transitionTimingFunction: "ease-in-out",
        transitionProperty: "background-color, border-color, outline, color, fill",
        transitionDuration: "150ms",

        borderRadius: borderRadius.small,

        whiteSpace: "nowrap",
        color: themeTokens.color.button.text.quiet,
        background: themeTokens.color.button.background.quiet.default,
        padding: space[8],

        ":hover": {
            background: themeTokens.color.button.background.quiet.hover,
            color: themeTokens.color.button.text.quiet,
        },

        ":focus-visible": {
            background: themeTokens.color.button.background.quiet.hover,
            color: themeTokens.color.button.text.quiet,
            outline: `solid ${borderWidth[2]} ${themeTokens.color.border.selected}`,
            outlineOffset: borderWidth[2],
        },
    }),
    inProgressMessage: css({
        textAlign: "center",
        marginBottom: space[32],
        color: themeTokens.color.text.primary,
    }),
    loading: css({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        img: {
            width: "18.75rem",
            height: "6.976875rem", // Using height to avoid screen jumping on slow-loading connections.
        },
    }),
    errorInfoPanel: css({
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
    }),
    errorInfoPanelDetails: css({
        shadow: themeTokens.shadow.small,
        width: "100%",
        textAlign: "left",
        backgroundColor: themeTokens.color.background.secondary.default,
        padding: space[8],
    }),
    errorMessage: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
    }),
};
